
import React from 'react';
import { format } from 'date-fns';
import { UserProfile } from '@/types/database.types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { UserRoleManager } from '@/components/users/UserRoleManager';
import {
  TableCell,
  TableRow,
} from '@/components/ui/table';
import { 
  MoreHorizontal, 
  UserCog,
  KeyRound,
  UserX, 
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface UserTableRowProps {
  profile: UserProfile;
  isCurrentUser: boolean;
  isUpdatingStatus: boolean;
  onStatusToggle: (userId: string, currentStatus: string) => void;
  onEditUser: (user: { id: string; firstName: string; lastName: string }) => void;
  onDeleteUser: (user: { id: string; email: string; name: string }) => void;
  onResetPassword: (user: { id: string; email: string; name: string }) => void;
}

export const UserTableRow: React.FC<UserTableRowProps> = ({
  profile,
  isCurrentUser,
  isUpdatingStatus,
  onStatusToggle,
  onEditUser,
  onDeleteUser,
  onResetPassword
}) => {
  const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
  const displayName = fullName || 'Unnamed User';
  
  return (
    <TableRow>
      <TableCell>{displayName}</TableCell>
      <TableCell>{profile.email || 'No email'}</TableCell>
      <TableCell>
        <UserRoleManager 
          userId={profile.id} 
          currentRole={profile.role} 
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center">
          {isUpdatingStatus ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Switch
              checked={profile.status === 'active'}
              onCheckedChange={() => onStatusToggle(profile.id, profile.status)}
              disabled={isCurrentUser}
            />
          )}
          <Badge 
            variant={profile.status === 'active' ? "success" : "secondary"}
            className="ml-2"
          >
            {profile.status === 'active' ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {format(new Date(profile.created_at), 'PP')}
      </TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem 
              onClick={() => onEditUser({
                id: profile.id,
                firstName: profile.first_name || '',
                lastName: profile.last_name || '',
              })}
            >
              <UserCog className="mr-2 h-4 w-4" />
              Edit Profile
            </DropdownMenuItem>
            
            <DropdownMenuItem
              onClick={() => onResetPassword({
                id: profile.id,
                email: profile.email || '',
                name: displayName,
              })}
            >
              <KeyRound className="mr-2 h-4 w-4" />
              Reset Password
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuItem
              onClick={() => onDeleteUser({
                id: profile.id,
                email: profile.email || '',
                name: displayName,
              })}
              className="text-destructive"
              disabled={isCurrentUser}
            >
              <UserX className="mr-2 h-4 w-4" />
              Delete User
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
